import React, { useEffect } from "react";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { Button, Checkbox, Input, Modal } from "react-daisyui";
import { Transition } from "@tailwindui/react";
import { useTranslation } from 'react-i18next';
import useFlatCountsByBudget from "../../../hooks/useFlatCountsByBudget";
import { Sparklines, SparklinesBars } from "react-sparklines";
import { useProfilePageValidationOptional } from "../components/ProfilePageValidationContext";

const freiburgAreas: { [key: string]: { [key: string]: { districts: string[] } } } = {
    'Freiburg im Breisgau': {
        'Mitte': {
            districts: ['Altstadt', 'Neuburg']
        },
        'Nord': {
            districts: ['Herdern', 'Zähringen', 'Brühl', 'Hochdorf']
        },
        'Ost': {
            districts: ['Waldsee', 'Littenweiler', 'Ebnet', 'Kappel']
        },
        'Süd': {
            districts: ['Oberau', 'Wiehre', 'Günterstal']
        },
        'West': {
            districts: ['Stühlinger', 'Mooswald', 'Betzenhausen', 'Landwasser', 'Lehen', 'Waltershofen', 'Mundenhof']
        },

        'Südwest': {
            districts: ['Haslach', 'St. Georgen', 'Opfingen', 'Tiengen', 'Munzingen', 'Weingarten', 'Rieselfeld', 'Vauban']
        },
        'Nordwest': {
            districts: ['Tiengen']
        }
    },
  'Nachbargemeinden': {
    'Vörstetten': {
      districts: []
    },
    'Gundelfingen': {
      districts: []
    },
    'Denzlingen': {
      districts: []
    },
    'Emmendingen': {
      districts: []
    },
    'Glottertal': {
      districts: []
    },
    'Stegen': {
      districts: []
    },
    'Kirchzarten': {
      districts: []
    },
    'Oberried': {
      districts: []
    },
    'Münstertal/Schwarzwald': {
      districts: []
    },
    'Bollschweil': {
      districts: []
    },
    'Horben': {
      districts: []
    },
    'Au': {
      districts: []
    },
    'Merzhausen': {
      districts: []
    },
    'Ebringen': {
      districts: []
    },
    'Schallstadt': {
      districts: []
    },
    'Bad Krozingen': {
      districts: []
    },
    'Breisach am Rhein': {
      districts: []
    },
    'Merdingen': {
      districts: []
    },
    'Gottenheim': {
      districts: []
    },
    'Umkirch': {
      districts: []
    },
    'March': {
      districts: []
    }
  }
};

const SearchPreferences: React.FC = () => {
    const { t } = useTranslation();
    const { localProfile, handleLocalProfileChange } = useUserProfile();
    const [areaDialogOpen, setAreaDialogOpen] = React.useState(false);
    const { flatCounts: initialFlatCounts } = useFlatCountsByBudget(localProfile?.location_preferences ?? undefined, localProfile?.room_preferences?.minRooms ?? undefined, localProfile?.size_preferences?.minSize ?? undefined);
    const flatCounts = initialFlatCounts?.slice(1); // Remove index 0 to show the chart correctly

    const { isPageValidated, setPageValidated } = useProfilePageValidationOptional();

    if (localProfile === undefined) {
        return null;
    }

    const handleAreaChange = (area: string, isChecked: boolean) => {
        let updatedPreferences = [...(localProfile.location_preference_areas ?? [])];

        if (isChecked) {
            if (area === 'Freiburg im Breisgau' || area === 'Nachbargemeinden') {
                // Add the main area and all its subareas
                const subareas = Object.keys(freiburgAreas[area]);
                updatedPreferences = [...new Set([...updatedPreferences, area, ...subareas])];
            } else {
                updatedPreferences = [...new Set([...updatedPreferences, area])];
            }
        } else {
            if (area === 'Freiburg im Breisgau' || area === 'Nachbargemeinden') {
                // Remove the main area and all its subareas
                const subareas = Object.keys(freiburgAreas[area]);
                updatedPreferences = updatedPreferences.filter(a => a !== area && !subareas.includes(a));
            } else {
                updatedPreferences = updatedPreferences.filter(a => a !== area);
            }
        }

        // Only update location_preference_areas
        handleLocalProfileChange('location_preference_areas', updatedPreferences);
    };

    const isAreaSelected = (area: string): boolean => {
        const selectedAreas = localProfile.location_preference_areas || [];
        return selectedAreas.includes(area);
    };

    const hasFlatType = localProfile.looking_for_shared_flat !== undefined && localProfile.looking_for_shared_flat !== null;
    const hasBudget = localProfile.budget_preferences?.max !== undefined && localProfile.budget_preferences?.max !== null && localProfile.budget_preferences?.max > 0;
    const hasLocationPreferences = (localProfile.location_preference_areas ?? []).length > 0;

    const isPageValid = hasFlatType && hasBudget && hasLocationPreferences;

    useEffect(() => {
        setPageValidated('SearchPreferences', isPageValid);
    }, [isPageValid]);

    return (
        <>
            <h1 className="text-3xl font-bold mb-6">{t('profilePages.searchPreferences.title')}</h1>

            {/* Property Type */}
            <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">{t('profilePages.searchPreferences.propertyType.title')}</h2>
                <div className="flex justify-between">
                    <Button
                        className="flex-1 mx-1 border border-primary"
                        data-umami-event="Search-preferences Property-type Change"
                        color={localProfile.looking_for_shared_flat === false ? 'primary' : 'ghost'}
                        onClick={() => handleLocalProfileChange('looking_for_shared_flat', false)}
                        data-testid="search-property-apartment"
                    >
                        {t('profilePages.searchPreferences.propertyType.apartment')}
                    </Button>
                    <Button
                        className="flex-1 mx-1 border border-primary"
                        data-umami-event="Search-preferences Property-type Change"
                        color={localProfile.looking_for_shared_flat === true ? 'primary' : 'ghost'}
                        onClick={() => handleLocalProfileChange('looking_for_shared_flat', true)}
                        data-testid="search-property-shared-flat"
                    >
                        {t('profilePages.searchPreferences.propertyType.sharedFlat')}
                    </Button>
                </div>
            </div>

            {/* Budget */}
            <Transition
                show={hasFlatType}
                enter="transition-opacity duration-500 ease-in-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-500 ease-in-out"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">{t('profilePages.searchPreferences.budget.title')}</h2>
                    <div className="flex flex-col space-y-4">
                        <div>
                            <div className="flex flex-row">
                            {flatCounts && (
                                <div className="relative h-20 overflow-hidden w-full mx-4 budget-chart">
                                    <div className="absolute inset-0" style={{ overflow: 'hidden' }}>
                                        <Sparklines data={flatCounts.map(item => item.flat_count)} width={300} height={80} preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                                            <SparklinesBars style={{fill: 'rgba(0, 0, 0, 0.1)'}} />
                                        </Sparklines>
                                    </div>
                                    <div 
                                        className="absolute inset-0" 
                                        style={{
                                            width: '100%',
                                            overflow: 'hidden',
                                            clipPath: `inset(0 ${100 - ((localProfile.budget_preferences?.max || 0) / 3000) * 100}% 0 0)`
                                        }}
                                    >
                                        <Sparklines data={flatCounts.map(item => item.flat_count)} width={300} height={80} preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                                            <SparklinesBars style={{fill: 'rgba(0, 0, 0, 0.5)'}} />
                                        </Sparklines>
                                    </div>
                                </div>
                            )}
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="3000"
                                className="range range-primary w-full"
                                value={localProfile.budget_preferences?.max || 0}
                                onChange={(e) => handleLocalProfileChange('budget_preferences', { ...localProfile.budget_preferences, max: parseInt(e.target.value) })}
                            />
                        </div>

                        <div className="flex justify-between">
                            <label className="input input-bordered flex items-center gap-2">
                                <input
                                    type="number"
                                    className="grow w-24"
                                    value={0}
                                    placeholder={t('profilePages.searchPreferences.budget.min')}
                                    disabled
                                />
                                <span className="">€</span>
                            </label>
                            <label className="input input-bordered flex items-center gap-2">
                                <input
                                    type="number"
                                    className="grow w-24"
                                    value={localProfile.budget_preferences?.max || ''}
                                    onChange={(e) => handleLocalProfileChange('budget_preferences', { ...localProfile.budget_preferences, max: parseInt(e.target.value) })}
                                    placeholder={t('profilePages.searchPreferences.budget.max')}
                                    data-testid="search-budget-max-input"
                                />
                                <span className="">€</span>
                            </label>
                        </div>
                    </div>
                </div>
            </Transition>

            {/* Regions */}
            <Transition
                show={hasFlatType && hasBudget}
                enter="transition-opacity duration-500 ease-in-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-500 ease-in-out"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">{t('profilePages.searchPreferences.regions.title')}</h2>
                    <div className="flex items-center space-x-2">
                        <Input
                            type="text"
                            value={localProfile.location_preference_areas?.join(', ') || t('profilePages.searchPreferences.regions.noRegionSelected')}
                            readOnly
                            className="flex-grow cursor-pointer"
                            data-umami-event="Search-preferences Regions Open"
                            onClick={() => setAreaDialogOpen(true)}
                        />
                        <Button
                            className="btn btn-outline"
                            data-umami-event="Search-preferences Regions Open"
                            onClick={() => setAreaDialogOpen(true)}
                            data-testid="search-region-open-button"
                        >
                            {t('profilePages.searchPreferences.regions.select')}
                        </Button>
                        <Modal open={areaDialogOpen}>
                            <Modal.Header className="font-bold">{t('profilePages.searchPreferences.regions.modalTitle')}</Modal.Header>
                            <Modal.Body className="max-w-[425px] max-h-[60vh] overflow-y-auto">
                                <p className="py-4">{t('profilePages.searchPreferences.regions.modalDescription')}</p>
                                <div className="grid gap-4">
                                    {Object.entries(freiburgAreas).map(([area, { _ }]) => (
                                        <React.Fragment key={area}>
                                            <div className="flex items-center mt-4">
                                                <Checkbox
                                                    id={area.replace(/\s/g, '-')}
                                                    checked={isAreaSelected(area)}
                                                    onChange={(e) => handleAreaChange(area, e.target.checked)}
                                                    className="mr-2"
                                                    data-testid={`search-region-checkbox-${area.replace(/\s/g, '-')}`}
                                                />
                                                <label htmlFor={area.replace(/\s/g, '-')}>{area}</label>
                                            </div>
                                            {Object.entries(freiburgAreas[area]).map(([subArea, { districts }]) => (
                                                <div key={subArea} className="flex items-center ml-6">
                                                    <Checkbox
                                                        id={subArea.replace(/\s/g, '-')}
                                                        checked={isAreaSelected(subArea)}
                                                        onChange={(e) => handleAreaChange(subArea, e.target.checked)}
                                                        className="mr-2"
                                                        data-testid={`search-region-checkbox-${subArea.replace(/\s/g, '-')}`}
                                                    />
                                                    <label htmlFor={subArea.replace(/\s/g, '-')}>
                                                        {subArea} {districts.length > 0 && `(${districts.join(', ')})`}
                                                    </label>
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </Modal.Body>
                            <Modal.Actions className="sticky">
                                <Button onClick={() => setAreaDialogOpen(false)} data-testid="search-region-close-button">{t('profilePages.searchPreferences.regions.close')}</Button>
                            </Modal.Actions>
                        </Modal>
                    </div>
                </div>
            </Transition>

            {/* Flat details */}
            <Transition
                show={hasFlatType && hasBudget && hasLocationPreferences}
                enter="transition-opacity duration-500 ease-in-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-500 ease-in-out"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">{t('profilePages.searchPreferences.flatDetails.title')}</h2>
                    <div className="flex flex-col space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-base">{t('profilePages.searchPreferences.flatDetails.rooms')}</span>
                            <div className="flex items-center space-x-2">
                                <Button
                                    size="sm"
                                    shape="circle"
                                    variant="outline"
                                    onClick={() => handleLocalProfileChange('room_preferences', { ...localProfile.room_preferences, minRooms: Math.max(1, (localProfile.room_preferences?.minRooms || 1) - 1) })}
                                    data-umami-event="Search-preferences Rooms Change"
                                >
                                    -
                                </Button>
                                <span className="text-lg w-10 text-center">{localProfile.room_preferences?.minRooms || 1}+</span>
                                <Button
                                    size="sm"
                                    shape="circle"
                                    variant="outline"
                                    onClick={() => handleLocalProfileChange('room_preferences', { ...localProfile.room_preferences, minRooms: Math.min(5, (localProfile.room_preferences?.minRooms || 1) + 1) })}
                                    data-umami-event="Search-preferences Rooms Change"
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base">{t('profilePages.searchPreferences.flatDetails.livingSpace')}</span>
                            <div className="flex items-center space-x-2">
                                <Button
                                    size="sm"
                                    shape="circle"
                                    variant="outline"
                                    onClick={() => handleLocalProfileChange('size_preferences', { ...localProfile.size_preferences, minSize: Math.max(10, (localProfile.size_preferences?.minSize || 10) - 5) })}
                                    data-umami-event="Search-preferences Apartment-size Change"
                                >
                                    -
                                </Button>
                                <span className="text-lg w-10 text-center">{localProfile.size_preferences?.minSize || 10}+</span>
                                <Button
                                    size="sm"
                                    shape="circle"
                                    variant="outline"
                                    onClick={() => handleLocalProfileChange('size_preferences', { ...localProfile.size_preferences, minSize: (localProfile.size_preferences?.minSize || 10) + 5 })}
                                    data-umami-event="Search-preferences Apartment-size Change"
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>

        </>
    );
};

export default SearchPreferences;
